/**
 * Every word a person reads in the Compliance section (#1426).
 *
 * Kept in one place for one reason: Corbin's rule that the section is "built
 * for a 7th grade education, so everyone that enters it can understand it."
 * `compliance-copy.test.ts` walks this table and fails the build when any
 * string scores above grade 7 on Flesch-Kincaid. A string that is not in this
 * table is a string nobody measured — put it here, not in the JSX.
 *
 * Vocabulary: the screens say "water provider", never "authority" or
 * "jurisdiction". Same thing, grade-5 word. `Authority` stays the code name.
 */

export const COMPLIANCE_COPY = {
    tile: {
        label: "Compliance",
        sub: "Licenses, city filing, and who we file with",
    },
    section: {
        crumbRoot: "More",
        title: "Compliance",
        groups: {
            work: "The work",
            setup: "Set-up",
        },
        tabs: {
            licenses: "Licenses & insurance",
            filing: "City filing",
            due: "What is due",
            owed: "Still owed",
            dueSoon: "soon",
            authorities: "Water providers",
            platforms: "Filing websites",
            licenseTypes: "License types",
            jurisdictions: "Which city a location is in",
            forms: "Report forms",
            woVendors: "Work-order vendors",
            gauges: "Test gauges",
            coi: "Insurance certificates",
            locker: "Document locker",
            packets: "City packets",
            schedules: "Reports that send themselves",
        },
        due: {
            title: "What is due",
            intro: "Devices due for a test, at accounts we have not been told to stop serving. This is a call list, not a work list. The customer has to book us.",
        },
        schedules: {
            title: "Reports that send themselves",
            intro: "A list you saved can go out by email on its own. Pick the day and the time. Pick who gets it.",
            empty: "Nothing is set up yet. Open a list, save it, then pick Send on a schedule.",
            loadFailed: "Could not load this page. Try again.",
            columns: {
                report: "Report",
                when: "When",
                goesTo: "Goes to",
                next: "Next send",
                last: "Last send",
                on: "On",
            },
            savedView: "saved list",
            surfaces: {
                obligations: "What is due",
                filings: "City filing",
                authorities: "Water providers",
                jobs: "Jobs",
                pricebook: "Fieldbook",
            },
            none: "—",
            never: "Not sent yet",
            skipped: "skipped, list was empty",
            failed: "did not go out",
            rows: (n: string) => `${n} rows`,
            oneRow: "1 row",
            countLine: (shown: string, total: string) => `Showing ${shown} of ${total}`,
            footnote: "A send skipped for an empty list says so. An empty inbox never means it broke.",
            turnedOff: "Off",
            change: "Change",
            stop: "Stop",
            stopAsk: "Stop sending this report?",
            stopped: "Stopped. It will not send again.",
            turnedOnToast: "On. It will send at the next time you picked.",
            turnedOffToast: "Off. It will not send until you turn it back on.",
            saveFailed: "That did not save. Try again.",
            link: {
                setUp: "Send on a schedule",
                summary: (when: string, who: string) => `${when} · ${who}`,
            },
            card: {
                title: "Pinned views",
                pin: "Pin",
                pinPrompt: "Name this list",
                pinned: "Pinned. You can send it on a schedule now.",
                pinFailed: "Could not pin that list. Try again.",
                removeAsk: "Remove this pinned list?",
                removed: "Removed.",
                cannotPin: "This tab cannot be pinned yet.",
                empty: "Pin a list to send it on a schedule.",
            },
        },
        dialog: {
            title: (name: string) => `Send "${name}" on a schedule`,
            howOften: "How often",
            daily: "Every day",
            weekly: "Every week",
            monthly: "Every month",
            whichDay: "Which day",
            whatTime: "What time",
            asLabel: "As",
            csv: "Spreadsheet (CSV)",
            pdf: "PDF",
            sendTo: "Send to",
            sendToNote: "Office and admin accounts only. Customers get their reports from the portal, not from here.",
            sendToEmpty: "No office accounts to send to yet.",
            skipEmpty: "Skip the send when the list is empty",
            skipEmptyWhy: "A weekly email that says nothing teaches you to skip the one that counts.",
            firstSend: "First send",
            firstSendLine: (when: string, count: string) =>
                `${when} · the same rows you see right now, ${count} of them`,
            firstSendCounting: "Counting the rows now…",
            firstSendNone: "We could not count the rows. The report will still go out.",
            cancel: "Cancel",
            start: "Start sending",
            save: "Save",
            saved: "Saved. The next send is on the way.",
            started: "Set up. The first one goes out at the time you picked.",
            pickOne: "Pick at least one person.",
        },
        settingsMoved: "The compliance rules moved. They now live in the Compliance section.",
        settingsMovedLink: "Open Compliance",
    },
    list: {
        title: "Water providers",
        intro: "The cities and water companies we send test reports to. A price you change here is for that one water provider. It lands in the Fieldbook too.",
        counts: {
            setUp: "Set up by us",
            setUpSub: "Ready to file with",
            attention: "Need attention",
            attentionSub: (noWay: number, notSetUp: number) =>
                `${noWay} have no way to file · ${notSetUp} have locations but aren't set up`,
            noFees: "No fees on file",
            noFeesSub: "Quotes there add no city fee. Often correct.",
            stateList: "On the state list",
            stateListSub: "Hidden until an admin turns one on",
        },
        search: "Type a city or water company…",
        sort: {
            attention: "Needs attention first",
            az: "A to Z",
            state: "State list",
        },
        columns: {
            provider: "Water provider",
            attention: "Needs attention",
            how: "How we file",
            adds: "A test there adds",
        },
        locations: (n: number) => (n === 1 ? "1 location" : `${n} locations`),
        fromStateList: "from the state list",
        covers: (name: string) => `covers ${name}`,
        attention: {
            notSet: "Not set",
            noWay: "We can't file here yet",
            notSetUp: "Not set up",
            none: "—",
        },
        how: {
            online: (platform: string) => `Online · ${platform}`,
            email: "Email",
            notSet: "Not set",
        },
        adds: {
            none: "$0 · no fees, that's normal",
            fireLine: (amount: string) => `fire line +${amount}`,
        },
        footer: "The ones that need attention float to the top. Click a row to open it.",
        showing: (shown: number, total: number) => `Showing ${shown} of ${total}`,
        empty: "No water provider matches that.",
        stateEmpty: "Nothing on the state list matches that.",
        addProvider: "Set up a new water provider",
        adminTools: "Set-up tools",
        loading: "Loading…",
        loadFailed: "Could not load the list. Try again.",
        retry: "Try again",
    },
    locker: {
        title: "Document locker",
        intro: "Drop each paper once. GWC reads it and uses it for every city.",
        neverHolds: "Nothing here stops a job, a test, a filing or a bill.",
        dropTitle: "Drop a PDF or a photo here",
        dropSub: "Insurance. Licenses. Gauge certs. IDs. Confined-space cards.",
        dropButton: "Pick files",
        reading: (name: string) => `Reading ${name}`,
        duplicate: (name: string) => `${name} is already in the locker. We kept the first copy.`,
        hiddenSaved: "Saved. Only admins can see driver's licenses.",
        readFailed: "GWC could not read this one. Fill in the orange boxes.",
        notConfigured: "Reading is off right now. Fill in the orange boxes.",
        checkTitle: "Check these",
        checkSub: "Only the orange boxes need you. The rest GWC read clearly.",
        looksRight: "Looks right",
        save: "Save",
        eachPlaceholder: "Each time",
        fileIt: "File it",
        stillOrange: (n: number) => (n === 1 ? "1 box to check" : `${n} boxes to check`),
        filed: "Filed. Every city packet now uses it.",
        readAgain: "Read again",
        takeOff: "Take off shelf",
        tookOff: "Taken off the shelf.",
        open: "Open file",
        addLine: "Add a line",
        removeLine: "Remove",
        pick: "Pick one",
        none: "Not on the paper",
        fields: {
            docType: "What it is",
            subject: "Whose it is",
            gauge: "Which gauge",
            issuedTo: "Insured",
            holder: "Certificate holder",
            issuer: "From",
            docNumber: "Number",
            issuedOn: "Issued",
            expiresOn: "Expires",
            limits: "Limits",
            additionalInsured: "Additional insured",
        },
        types: {
            coi: "Insurance certificate",
            bpat_license: "BPAT license",
            gauge_calibration: "Gauge cert",
            drivers_license: "Driver's license",
            confined_space: "Confined-space card",
            other: "Something else",
            unknown: "Not sure yet",
        },
        coverages: {
            general_liability: "General liability",
            auto: "Auto",
            workers_comp: "Workers' comp",
            umbrella: "Umbrella",
            professional: "Professional",
            other: "Other",
        },
        shelves: {
            title: "Shelves",
            coi: "Insurance certificates",
            coiSub: "One per city holder. Each city wants its own name on it.",
            coiEmpty: "No insurance certificates yet. Drop the one from our agent.",
            bpat: "BPAT licenses",
            bpatSub: "Dates come from Licenses and CE.",
            gauge: "Gauge certs",
            gaugeSub: "Dates come from Test gauges. Cities want the PDF.",
            dl: "Driver's licenses",
            dlSub: "Only admins see this shelf.",
            cs: "Confined-space cards",
            csSub: "Fort Worth, Irving and Keller ask for one.",
        },
        slot: {
            onFile: "On file",
            needPdf: "Have dates, need the PDF",
            soon: (days: number) => `Expires in ${days} days`,
            expired: "Expired",
            empty: "Drop one",
            due: (date: string) => `Due ${date}`,
            exp: (date: string) => `Expires ${date}`,
        },
        legend: {
            onFile: "On file",
            half: "Need the PDF",
            soon: "Expires soon",
            empty: "Empty",
        },
    },
    packets: {
        title: "City packets",
        intro: "Each city wants its own papers. Pick a city and GWC builds the packet.",
        neverHolds: "A missing packet never stops a job, a test, a filing or a bill.",
        askAgent: "Ask our agent for papers",
        build: "Build packet",
        jobs: (n: number) => `${n.toLocaleString()} jobs a year`,
        noCities: "No city has its steps set up yet.",
        legendNote:
            "L is the license. G is the gauge cert. I is insurance. D is an ID. C is confined space. F is the city form. W means Fort Worth first.",
        methods: { portal: "Website", email: "Email", in_person: "In person" },
        fee: (f: string) => `Fee: ${f}`,
        feeUnknown: "Fee: ask the city",
        renews: (r: string) => `Renews: ${r}`,
        tester: "For which tester",
        missing: (n: number) =>
            n === 1
                ? "1 thing is missing. You can still send what is ready."
                : `${n} things are missing. You can still send what is ready.`,
        allReady: "Everything is here.",
        fixes: { drop: "Drop it", ask_agent: "Ask agent", do_fort_worth: "Do Fort Worth", fill_by_hand: "By hand" },
        ready: "Ready to send",
        copyEmail: "Copy email",
        copy: "Copy",
        copied: "Copied",
        to: "To",
        subject: "Subject",
        noTo: "Ask the city where to send it",
        files: "Files for this packet",
        openFile: "Open",
        noFile: "Not in the locker",
        where: "Who to reach",
        phone: "Phone",
        emailLabel: "Email",
        address: "Address",
        cityTold: "What the city told us",
        cityToldSub: "Fill in what you learn. The packet updates.",
        feeLabel: "Fee",
        holderLabel: "Certificate holder",
        minLabel: "Lowest limit each time",
        save: "Save",
        saved: "Saved",
        questions: "Still to ask the city",
        answered: "Answered",
        agentTitle: "Papers from our agent",
        agentSub: "Each city wants its own name on the certificate. One email asks for all of them.",
        agentName: "Agent name",
        agentEmail: "Agent email",
        agentMissing: "Add our agent's email so the draft is ready to send.",
        never: "GWC never sends this for you.",
        sentIt: "I sent it",
        markedSent: "Marked sent. The job warning now says we are waiting.",
        gotNumber: "Got the city's number back?",
        regNumber: "City number",
        regEnds: "Ends",
        markRegistered: "Save, mark registered",
        markedRegistered: "Saved. Jobs in this city stop warning about this tester.",
        regStatus: {
            none: "Not on file",
            sent: "Sent, waiting on the city",
            registered: "Registered",
        },
        jobCard: {
            title: (city: string) => `Heads up: ${city} registration`,
            warnOnly: "Heads up only. This never holds the job: it can still be sent out, tested and billed.",
            open: (city: string) => `Open the ${city} packet`,
            info: (city: string) => ({
                title: "Why this is a heads up",
                lines: [
                    `${city} wants the tester registered and an insurance certificate on file.`,
                    "It's worth fixing, but it never holds this job. The job can still go out, be tested, filed and billed.",
                    'What holds a job is at the top of the page, under "This job can\'t go out yet".',
                ],
            }),
        },
        statuses: {
            have: "In the locker",
            dates_only: "Dates on file, need the PDF",
            missing: "Missing",
            check: "Check it",
            by_hand: "By hand",
        },
    },
    detail: {
        back: "All water providers",
        status: {
            ok: "Nothing missing",
            missing: (n: number) => (n === 1 ? "1 thing missing" : `${n} things missing`),
            notSetUp: "Not set up yet",
        },
        permission: {
            office: "You can change prices here. Adding a fee, or changing how we file, needs an admin.",
            admin: "You can change prices, add a fee, and change how we file.",
            readOnly: "You can read this page. Changing a price needs pricing access.",
        },
        glance: {
            how: "How we file",
            howOnline: (platform: string) => `Online · ${platform}`,
            howEmail: "Email",
            howNotSet: "Not set",
            open: (platform: string) => `Open ${platform}`,
            accountNote: "Needs the water account number for that address.",
            emailNote: (email: string) => `Reports go to ${email}.`,
            notSetNote: "Nobody has set how we file here. Reports can't go out yet.",
            adds: "A test here adds",
            addsBreakdown: (parts: string[]) => parts.join(" + "),
            fireLine: (amount: string) => `Fire line adds ${amount} more.`,
            noFees: "No fees here. Quotes add nothing for the city.",
            changePrice: "Change a price ↓",
            missing: "Anything missing?",
            no: "No",
            allOnFile: "Website, fees and contact are on file. Reports can go out.",
            yes: (n: number) => `Yes, ${n}`,
        },
        missingItems: {
            filing: "How we file",
            contact: "A contact",
            website: "A website",
        },
        fees: {
            title: "Fees",
            intro: "Click a price to change it. The new price is for this water provider only. It lands in the Fieldbook and in the history.",
            introReadOnly: "Prices are set in the Fieldbook. Ask the office to change one.",
            onEveryTest: "on every test",
            fireOnly: "fire line tests only",
            limitedTo: (codes: string) => `only on ${codes}`,
            role: {
                city_fee: "City fee",
                admin_fee: "Admin fee",
                surcharge: "Extra charge",
                permit: "Permit",
                other: "Fee",
            },
            clickToChange: "Click to change",
            sharedChip: (n: number) => (n === 1 ? "shared with 1 other" : `shared with ${n} others`),
            save: "Save",
            saveOnly: (name: string) => `Save for ${name} only`,
            cancel: "Cancel",
            saving: "Saving…",
            sharedNote: (n: number, oldPrice: string, newPrice: string, name: string) =>
                `This fee is shared. ${n} other water ${n === 1 ? "provider uses" : "providers use"} it at ${oldPrice}. Saving sets ${newPrice} for ${name} only. The other ${n} ${n === 1 ? "keeps" : "keep"} ${oldPrice}. To change it for everyone, use the Fieldbook.`,
            saved: "Price saved. It shows on every new quote from now on.",
            savedOnly: (name: string) => `Price saved for ${name} only.`,
            saveFailed: "The price did not save. Try again.",
            badAmount: "Type a dollar amount first.",
            none: "No fees on file. Quotes here add nothing for the city.",
            askAdmin: "To add or remove a fee, ask an admin.",
            addFee: "Add a fee",
            done: "Done",
            // A different city fee for homes and businesses (migration 717).
            split: {
                switchLabel: "Different fee for homes and businesses",
                off: "Off. Every customer pays the same city fee.",
                on: "On. Homes pay one city fee. Businesses pay the other.",
                homes: "Homes",
                businesses: "Businesses",
                save: "Save",
                saving: "Saving…",
                saved: "Saved. New quotes and bills use these fees.",
                turnedOff: "Off. Everyone pays the business fee now.",
                saveFailed: "That did not save. Try again.",
                needBoth: "Type both amounts first.",
                homesOnly: "homes only",
                businessesOnly: "businesses only",
                homesPay: (amount: string) => `Homes pay ${amount} instead.`,
                askOffice: "To change this, ask the office.",
                help: {
                    title: "Homes and businesses",
                    lines: [
                        "Some cities charge a home a smaller city fee than a business.",
                        "Turn this on and type both amounts.",
                        "We check the Type on the customer page. It says Residential or Commercial.",
                        "No type set? We charge the business fee. The fee line then asks home or business. One tap fixes it.",
                    ],
                },
            },
        },
        filing: {
            title: "How to file here",
            website: "Website",
            emailTo: "Email reports to",
            fail: "If a device fails",
            failShutoff: "They shut off water until it is fixed and retested.",
            failNone: "No special rule. Fix it and retest.",
            form: "Form we send",
            formNone: "Not set",
            contact: "Their contact",
            contactNone: "Not on file",
            onsite: "Techs can send the report from the site.",
            edit: "Edit",
        },
        more: {
            title: "More about this provider",
            hint: "Names · state ID · their rules · notes",
            nameMenus: "Name in menus",
            nameReports: "Name on reports",
            stateId: "State ID (PWS)",
            address: "Mailing address",
            rules: "Their rules",
            reading: (step: string) => `Gauge readings go in steps of ${step} psi.`,
            points: (mult: string) => `Tests here count ${mult}× for tech points.`,
            notes: "Notes",
            none: "Not on file",
            edit: "Edit",
        },
        owed: {
            title: "What is still owed",
            intro: "Work we owe that has not been filed yet, and how long filings sit with us before they go out.",
            tiles: {
                stillOwed: "Still owed",
                stillOwedNote: "Due or overdue, not filed yet",
                overdue: "Overdue",
                overdueNote: "Past their date, no passing test",
                dueSoon: "Due in 60 days",
                dueSoonNote: "Call list for the next two months",
                noDueDate: "No due date",
                noDueDateNote: "Fix on the device",
                noProvider: "No water provider",
                noProviderNote: "Fix on the location",
            },
            apart: "The last two are counted apart on purpose. A device with no date and a location with no water provider are different jobs to fix.",
            latency: {
                title: "How long a filing sits with us",
                typical: "Typical",
                typicalNote: "Half of filings go out within this many days",
                slow: "Slow ones",
                slowNote: "9 in 10 go out within this many days",
                testToFiled: "Test to filed",
                testToFiledNote: "What the customer waits, start to finish",
                days: (n: string) => `${n} days`,
                oneDay: "1 day",
                sameDay: "Same day",
                none: "\u2014",
                emptyTitle: "Nothing measured yet.",
                emptyBody:
                    "Counting starts today. Old filings are not counted back, because we did not record when they became ready. A made up number would look real and be wrong.",
                counted: (n: string) => `From ${n} filings`,
                countedOne: "From 1 filing",
                tooFew: (n: string) => `Needs ${n} filings before we can say`,
                byProvider: "By water provider",
                cols: {
                    provider: "Water provider",
                    filings: "Filings",
                    typical: "Typical",
                    slow: "Slow ones",
                    how: "How we file",
                },
                howNotSet: "Not set",
            },
            table: {
                title: "Who we owe",
                cols: {
                    customer: "Customer",
                    devices: "Devices",
                    overdue: "Overdue",
                    oldest: "Oldest due date",
                    providers: "Water providers",
                },
                noProviderBadge: (n: string) => `${n} places with no water provider`,
                noProviderBadgeOne: "1 place with no water provider",
                daysAgo: (n: string) => `${n} days ago`,
                notSet: "Not set",
                none: "None",
                open: "Open",
                empty: "Nothing is owed right now.",
                emptyNote: "Every device with a date is filed or not due yet.",
                exportAll: (n: string) => `Export all ${n}`,
            },
            loadFailed: "Could not load what is still owed. Try again.",
        },
        rules: {
            title: "Test rules",
            hint: "How often devices here must be tested.",
            empty: "No test rules for this water provider.",
            emptyNote: "Devices here use the number set on the device, or 12 months.",
            add: "Add a rule",
            everyMonths: (n: string) => `Test every ${n} months`,
            anyHazard: "Any hazard",
            anyType: "Any device type",
            hazard: (h: string) => `${h} hazard`,
            flag: (d: string) => `Flag as due ${d} days ahead`,
            fileWithin: (d: string) => `File within ${d} days of the test`,
            governs: (n: string) => `${n} devices follow this rule`,
            governsOne: "1 device follows this rule",
            source: "Where this rule comes from",
            remove: "Remove",
            confirmRemove: "Remove this rule? Devices keep the dates they have.",
            removed: "Rule removed.",
            loadFailed: "Could not load the test rules.",
            form: {
                title: "New test rule",
                hazardLabel: "Applies to hazard level",
                typeLabel: "Applies to device type",
                every: "Test every",
                months: "months",
                flag: "Flag as due",
                daysAhead: "days ahead",
                file: "File within",
                daysOfTest: "days of the test",
                optional: "optional",
                sourceLabel: "Where this rule comes from",
                sourceHint: "A web page or a letter. Not required.",
                save: "Save rule",
                cancel: "Cancel",
                note: "Saving stores the rule. It does not touch a device yet. That is the next step, so undo means something.",
                saved: "Rule saved. No device has changed yet.",
                exists: "A rule for that hazard and device type is already here.",
                failed: "Could not save the rule.",
                badMonths: "Months must be a whole number from 1 to 120.",
            },
            hazardOptions: { any: "Any", high: "High", low: "Low" },
            typeOptions: {
                any: "Any",
                backflow: "Backflow",
                extinguisher: "Extinguisher",
                e_light: "Exit light",
                hood: "Hood",
                hydrant: "Hydrant",
            },
            preview: {
                open: "Apply\u2026",
                title: "Apply this rule. What will happen:",
                intro: "Not \u201care you sure?\u201d. The breakdown, before anything is written.",
                governed: "devices this rule governs",
                overdue: "become overdue today",
                overdueWhy: (m: string) =>
                    `Tested more than ${m} months ago. They are overdue under the new rule, and that is right. But they all land on the list at once.`,
                gaining: "get a due date for the first time",
                earlier: "move earlier, still in the future",
                later: "move later",
                unchanged: "are already on the right date",
                override: "have a hand-set number on the device",
                overrideWhy:
                    "Somebody set these on purpose. They are in, because the rule applies to all. Untick to leave them alone.",
                include: "Include them",
                noPass: "have no passing test on file. Left alone.",
                noPassWhy: "Nothing to count from. We don't guess, and we don't blank the date.",
                window: "land inside a customer text window",
                windowWhy:
                    "The 90, 30 and 7 day \u201cyour device is due\u201d text fires on these dates. Those texts are off today. This number needs to be on screen before they are turned on.",
                apply: (n: string) => `Apply to ${n} devices`,
                applyOne: "Apply to 1 device",
                nothing: "Nothing to change",
                notNow: "Not now",
                footnote:
                    "One action, all or nothing. Saved as a batch you can undo, with no time limit. The reason to undo a date usually turns up weeks later.",
                applied: (n: string) => `Done. ${n} devices moved to their new dates.`,
                appliedNone: "Done. No device needed to move.",
                applyFailed: "Could not apply the rule. Nothing was changed.",
                loadFailed: "Could not build the preview.",
                show: "Show the devices",
                hide: "Hide the devices",
                more: (n: string) => `and ${n} more`,
                cols: {
                    device: "Device",
                    where: "Location",
                    who: "Customer",
                    now: "Now",
                    next: "New date",
                    why: "Why",
                },
                noDate: "no date",
                buckets: {
                    becoming_overdue: "Becomes overdue",
                    gaining_date: "Gets a date",
                    moving_earlier: "Earlier",
                    moving_later: "Later",
                    unchanged: "No change",
                    no_passing_test: "No passing test",
                },
                excluded: "Left alone (hand-set)",
            },
            batches: {
                title: "Applied",
                none: "This rule has not been applied yet.",
                applied: (n: string, when: string) => `${n} devices moved on ${when}`,
                putBack: (n: string, when: string) => `${n} devices put back on ${when}`,
                by: (who: string) => `by ${who}`,
                undo: "Undo",
                undone: "Undone",
                confirmUndo: (n: string) => `Put ${n} devices back on the dates they had?`,
                undoDone: (n: string) => `${n} devices are back on their old dates.`,
                undoFailed: "Could not undo this batch.",
            },
        },
        history: {
            title: "What changed",
            empty: "No changes recorded yet.",
            note: "Every price and filing change lands here on its own. Nobody can edit or delete this list.",
            system: "System update",
            unknown: "Unknown",
        },
        notFound: "That water provider is not here.",
        loadFailed: "Could not load this water provider. Try again.",
        noAccess: "You don't have access to this page. Ask an admin for compliance access.",
    },
} as const;

/**
 * Words that are not allowed on these screens. They are the words the code
 * uses; the office does not. The copy test scans both this table and the
 * page sources for them.
 */
export const BANNED_WORDS = ["authority", "jurisdiction", "PWS ID", "submission platform", "purveyor"] as const;
